import {useEffect} from 'react'
import {useSnapshot} from 'valtio'
import {retryPayment, store, t, utils} from '@/form.js'
import {formatAmount} from '@/config/amount.js'
import {AppButton} from '@/components/AppButton/AppButton.jsx'
import {Spinner} from '@/components/Spinner/Spinner.jsx'
import {FailedIcon, SuccessIcon} from '@/components/StatusIcon/StatusIcon.jsx'
import styles from './CardStatusPage.module.css'

const {sbankStatuses} = utils

const POLL_INTERVAL = 3e3

const TONE = {
  [sbankStatuses.COMPLETE]: styles.success,
  [sbankStatuses.PARTIAL_COMPLETE]: styles.success,
  [sbankStatuses.FAILED]: styles.danger,
}

export function CardStatusPage() {
  const appState = useSnapshot(store.appSlice.state)
  const formState = useSnapshot(store.cardFormSlice.state)

  const {params} = appState

  const finishUrl = params.finish_url
    || formState.update.data?.params?.finish_url
    || formState.status?.data?.finish_url

  const paymentStatus = formState.status?.data?.payment_status ?? sbankStatuses.PENDING
  const isPending = !(paymentStatus in TONE)

  // Statuses we have no screen for (expired, refunded, …) keep polling and
  // borrow the pending copy, rather than rendering a raw translation key.
  const copyStatus = isPending ? sbankStatuses.PENDING : paymentStatus

  const amount = formatAmount(formState.formInfo)

  useEffect(() => {
    store.cardFormSlice.actions.fetchStatus()
    const intervalId = setInterval(() => {
      store.cardFormSlice.actions.fetchStatus()
    }, POLL_INTERVAL)
    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return (
    <div className={styles.status}>
      <div className={styles.icon}>
        {isPending && <Spinner size={64} thickness={3}/>}
        {TONE[paymentStatus] === styles.success && <SuccessIcon/>}
        {paymentStatus === sbankStatuses.FAILED && <FailedIcon/>}
      </div>

      <h2 className={styles.title}>
        {t(`PaymentStatus:Title.${copyStatus}`)}
      </h2>

      <p className={styles.text}>
        {t(`PaymentStatus:Text.${copyStatus}`, {amount})}
      </p>

      {!isPending && (
        <div className={`${styles.amountBox} ${TONE[paymentStatus]}`}>
          <p className={`caption ${styles.amountLabel}`}>
            {t(`PaymentStatus:AmountLabel.${copyStatus}`)}
          </p>
          <p className={styles.amountValue}>
            {amount}
          </p>
        </div>
      )}

      {!isPending && (finishUrl || paymentStatus === sbankStatuses.FAILED) && (
        <div className={styles.actions}>
          {finishUrl && (
            <AppButton
              variant={paymentStatus === sbankStatuses.FAILED ? 'secondary' : 'primary'}
              onClick={() => utils.redirect(finishUrl)}
            >
              {t('Common:ReturnToShop')}
            </AppButton>
          )}

          {paymentStatus === sbankStatuses.FAILED && (
            <AppButton onClick={retryPayment}>
              {t('Action:Retry')}
            </AppButton>
          )}
        </div>
      )}
    </div>
  )
}
